import {
  Button,
  Form,
  Input,
  type InputProps,
  Modal,
  type ModalProps,
  Select,
  type SelectProps,
  Switch,
  type SwitchProps,
} from "antd";
import { Rule } from "antd/es/form";
import { TextAreaProps } from "antd/es/input";
import { Key, useEffect } from "react";

interface BaseFieldProps<T> {
  label: string;
  name: keyof T;
  rules?: Rule[];
  tooltip?: string;
}

export type FieldProps<T> =
  | (BaseFieldProps<T> & { type: "text"; inputProps?: InputProps })
  | (BaseFieldProps<T> & { type: "select"; selectProps?: SelectProps })
  | (BaseFieldProps<T> & { type: "textarea"; inputProps?: TextAreaProps })
  | (BaseFieldProps<T> & { type: "switch"; switchProps?: SwitchProps });

function renderFormItemControl<T extends object>(
  field: FieldProps<T>,
  isEdited: boolean,
) {
  const formItemComponents: Record<FieldProps<T>["type"], JSX.Element> = {
    text: (
      <Input
        {...(field as Extract<FieldProps<T>, { type: "text" }>).inputProps}
      />
    ),
    select: (
      <Select
        {...(field as Extract<FieldProps<T>, { type: "select" }>).selectProps}
      />
    ),
    textarea: (
      <Input.TextArea
        rows={4}
        {...(field as Extract<FieldProps<T>, { type: "textarea" }>).inputProps}
      />
    ),
    switch: (
      <Switch
        {...(field as Extract<FieldProps<T>, { type: "switch" }>).switchProps}
      />
    ),
  };

  return (
    <Form.Item
      key={field.name as Key}
      name={field.name as string}
      rules={field.rules}
      label={
        <span>
          {field.label} {isEdited && "*"}
        </span>
      }
      shouldUpdate
      tooltip={field.tooltip}
      valuePropName={field.type === "switch" ? "checked" : undefined}
    >
      {formItemComponents[field.type]}
    </Form.Item>
  );
}

interface Props<TFields extends object> extends ModalProps {
  onConfirm: (data: TFields) => void;
  mode?: "create" | "edit";
  fields: FieldProps<TFields>[];
  onEdit?: () => void;
  isLoading?: boolean;
  isEditing?: boolean;
  initialValues?: Partial<TFields>;
}

export default function ModalWithForm<TFields extends object>({
  fields,
  onCancel,
  onConfirm,
  mode = "create",
  isEditing = false,
  isLoading,
  onEdit,
  initialValues,
  ...props
}: Props<TFields>) {
  const [form] = Form.useForm();
  const formValues = Form.useWatch([], form);

  useEffect(() => {
    if (props.open) {
      if (initialValues) {
        form.setFieldsValue(initialValues);
      } else {
        form.resetFields();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  return (
    <Modal
      classNames={{
        body: "max-h-[80vh] overflow-y-scroll",
      }}
      centered
      footer={null}
      onCancel={onCancel}
      closable={!isLoading}
      afterClose={() => {
        form.resetFields();
      }}
      {...props}
    >
      <div className="mt-8">
        {mode === "edit" && (
          <div className="flex justify-end">
            <Button
              htmlType="button"
              onClick={onEdit}
              className="bg-primary text-white"
            >
              Edit
            </Button>
          </div>
        )}
        <Form
          form={form}
          initialValues={initialValues}
          layout="vertical"
          onFinish={onConfirm}
          preserve={false}
        >
          {fields.map((field) =>
            renderFormItemControl(
              field,
              isEditing &&
                initialValues &&
                formValues &&
                formValues[field.name as keyof TFields] !==
                  initialValues[field.name as keyof TFields],
            ),
          )}
          {(mode === "create" || isEditing) && (
            <div className="grid grid-cols-2 gap-4">
              <Button disabled={isLoading} htmlType="button" onClick={onCancel}>
                Cancel
              </Button>

              <Form.Item className="w-full">
                <Button
                  className="w-full bg-primary text-white"
                  variant="solid"
                  color="primary"
                  htmlType="submit"
                  loading={isLoading}
                >
                  Confirm
                </Button>
              </Form.Item>
            </div>
          )}
        </Form>
      </div>
    </Modal>
  );
}
